import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import {
  Button,
  PlusCircleIcon,
  SegmentControl,
  Star1Icon,
  useHapticFeedback,
} from '@blade/ui-kit'
import { walletsAtom } from '@root/entities/wallet/atoms'
import { reatomAddWalletDrawerOpenedAtom } from '@root/features/wallet'
import { WalletsList } from '@root/features/wallet/wallets-list/ui/wallets-list'
import { useTranslation } from '@root/shared/localization'
import { Drawer } from '@root/shared/ui-kit'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { WalletId } from '@root/entities/wallet/model'
import type { DrawerProps } from '@root/shared/ui-kit'

type WalletsSet = 'all' | 'favorite'

type WalletSelectorDrawerProps = Pick<DrawerProps, 'onOpenChange' | 'open'> & {
  title?: string
  selected?: WalletId
  wallets?: Array<WalletModel>
  onWalletClick?: (wallet: WalletModel) => void
}

export const WalletSelectorDrawer: React.FC<WalletSelectorDrawerProps> = ({
  open,
  wallets: walletsProp,
  onOpenChange,
  onWalletClick,
  title,
  selected,
}) => {
  const [userWallets] = useAtom(walletsAtom)
  const haptics = useHapticFeedback()
  const { t } = useTranslation()
  const [walletsSet, setWalletsSet] = useState<WalletsSet>('all')
  const toggleAddWalletDrawer = useAction(
    reatomAddWalletDrawerOpenedAtom.toggle,
  )
  const wallets = walletsProp ?? userWallets

  const [filteredWallets] = useAtom(
    (ctx) => {
      if (walletsSet === 'favorite') {
        return wallets.filter((wallet) => ctx.spy(wallet.favorite))
      }
      return wallets
    },
    [wallets, walletsSet],
  )

  const walletsReordered = useMemo(() => {
    const activeWallet = filteredWallets.find(
      (wallet) => wallet.id === selected,
    )
    if (!activeWallet) {
      return filteredWallets
    }

    return [
      activeWallet,
      ...filteredWallets.filter((wallet) => wallet.id !== activeWallet.id),
    ]
  }, [filteredWallets, selected])

  return (
    <Root
      open={open}
      onOpenChange={onOpenChange}
      title={title ?? t('common.wallets')}
    >
      <Content>
        <Tabs>
          <SegmentControl value={walletsSet} onChange={setWalletsSet}>
            <SegmentControl.Item value={'all' satisfies WalletsSet}>
              {t('wallet.all_wallets')}
            </SegmentControl.Item>
            <SegmentControl.Item value={'favorite' satisfies WalletsSet}>
              <FavoriteTab>
                <Star1Icon size={14} />
                {t('wallet.favorite')}
              </FavoriteTab>
            </SegmentControl.Item>
          </SegmentControl>
        </Tabs>
        <WalletsList
          wallets={walletsReordered}
          selectedWalletId={selected}
          onWalletSelected={(wallet) => {
            if (!onWalletClick) {
              return
            }
            haptics.impactOccurred('light')
            onWalletClick(wallet)
          }}
        />
        <Footer>
          <Button
            iconLeft={<PlusCircleIcon size={16} />}
            type="button"
            radius="rounded"
            color="primary"
            fullWidth
            onClick={() => {
              onOpenChange?.(false)
              toggleAddWalletDrawer()
            }}
          >
            {t('wallet.add_wallet')}
          </Button>
        </Footer>
      </Content>
    </Root>
  )
}

const Root = styled(Drawer)`
  height: 100%;
  padding-bottom: var(--safe-area-inset-bottom);
`

const Footer = styled.div`
  padding: 0 12px calc(var(--safe-area-inset-bottom) + 12px);
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
`

const Tabs = styled.div`
  display: grid;
  justify-content: center;
`

const FavoriteTab = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
`
